import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, ArrowDown, ArrowUp, PiggyBank, TrendingUp } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useSoldes } from '@/hooks/queries/useSoldes';
import { useCashflowPrevisionnel } from '@/hooks/queries/useCashflow';
import { formatDA, formatDACompact, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';

const RANGE_OPTIONS = [
  { value: 30, label: t('echeances.cashflow.horizon30') },
  { value: 60, label: t('echeances.cashflow.horizon60') },
  { value: 90, label: t('echeances.cashflow.horizon90') },
];

export function CashflowPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  const [days, setDays] = useState(30);
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: soldes = [] } = useSoldes(entrepriseId);
  const { data: cashflow = [], isLoading } = useCashflowPrevisionnel(entrepriseId, days);

  const soldeActuel = soldes
    .filter((s) => s.devise_code === 'DZD')
    .reduce((acc, s) => acc + s.solde_courant, 0);

  const totalEnc = cashflow.reduce((acc, e) => acc + Number(e.encaissements_jour), 0);
  const totalDec = cashflow.reduce((acc, e) => acc + Number(e.decaissements_jour), 0);
  const projete = soldeActuel + totalEnc - totalDec;

  // Données du graphique : cumul progressif
  const chartData = useMemo(() => {
    let cumulSolde = soldeActuel;
    return cashflow.map((d) => {
      const enc = Number(d.encaissements_jour);
      const dec = Number(d.decaissements_jour);
      cumulSolde += enc - dec;
      return {
        jour: d.jour,
        jourLabel: formatDateDZ(d.jour),
        encaissements: enc,
        decaissements: -dec, // pour affichage en bas du graphique
        soldeProjete: cumulSolde,
      };
    });
  }, [cashflow, soldeActuel]);

  const minSolde = chartData.reduce((m, d) => Math.min(m, d.soldeProjete), soldeActuel);
  const alerteSoldeNegatif = minSolde < 0;
  const alerteSoldeFaible = minSolde >= 0 && minSolde < 50000;

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('echeances.cashflow.title')}
        subtitle={t('echeances.cashflow.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          icon={<PiggyBank className="h-4 w-4" />}
          label={t('echeances.cashflow.soldeActuel')}
          value={formatDA(soldeActuel)}
          tone={soldeActuel < 0 ? 'destructive' : 'default'}
        />
        <KpiCard
          icon={<ArrowDown className="h-4 w-4" />}
          label={t('echeances.cashflow.encaissementsAVenir')}
          value={formatDA(totalEnc)}
          tone="success"
        />
        <KpiCard
          icon={<ArrowUp className="h-4 w-4" />}
          label={t('echeances.cashflow.decaissementsAVenir')}
          value={formatDA(totalDec)}
          tone="warning"
        />
        <KpiCard
          icon={<TrendingUp className="h-4 w-4" />}
          label={t('echeances.cashflow.soldeProjete')}
          value={formatDA(projete)}
          tone={projete < 0 ? 'destructive' : projete < 50000 ? 'warning' : 'success'}
        />
      </div>

      {alerteSoldeNegatif && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
          <div>
            <div className="text-sm font-semibold text-destructive">
              {t('echeances.cashflow.alerteSoldeNegatif')}
            </div>
            <div className="text-xs text-muted-foreground">
              Minimum projeté : <span className="num font-medium">{formatDA(minSolde)}</span>.
              Concentrez les relances sur les retards et négociez les délais fournisseurs.
            </div>
          </div>
        </div>
      )}
      {alerteSoldeFaible && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-warning" />
          <div className="text-sm">
            {t('echeances.cashflow.alerteSoldeFaible')} —{' '}
            <span className="num font-medium">{formatDA(minSolde)}</span>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 text-sm font-semibold">Évolution prévisionnelle</div>
        <div className="h-[360px] w-full">
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Chargement…
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="jourLabel"
                  tick={{ fontSize: 10 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  tickFormatter={(v: number) => formatDACompact(v)}
                  tick={{ fontSize: 10 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip
                  formatter={(v: number) => formatDA(v)}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="encaissements" name="Encaissements" fill="hsl(var(--success))" />
                <Bar dataKey="decaissements" name="Décaissements" fill="hsl(var(--warning))" />
                <Line
                  type="monotone"
                  dataKey="soldeProjete"
                  name="Solde projeté"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="mb-3 text-sm font-semibold">Détail jour par jour</div>
        <div className="scrollbar-thin max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2">Jour</th>
                <th className="py-2 text-right">Encaiss.</th>
                <th className="py-2 text-right">Décaiss.</th>
                <th className="py-2 text-right">Flux net</th>
                <th className="py-2 text-right">Solde projeté</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((d) => (
                <tr key={d.jour} className="border-b border-border/40">
                  <td className="num py-1">{d.jourLabel}</td>
                  <td className="num py-1 text-right text-success">
                    {d.encaissements > 0 ? formatDA(d.encaissements) : '—'}
                  </td>
                  <td className="num py-1 text-right text-warning">
                    {d.decaissements < 0 ? formatDA(-d.decaissements) : '—'}
                  </td>
                  <td
                    className={cn(
                      'num py-1 text-right',
                      d.encaissements + d.decaissements < 0 && 'text-destructive',
                    )}
                  >
                    {formatDA(d.encaissements + d.decaissements)}
                  </td>
                  <td
                    className={cn(
                      'num py-1 text-right font-medium',
                      d.soldeProjete < 0 && 'text-destructive',
                    )}
                  >
                    {formatDA(d.soldeProjete)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: JSX.Element;
  label: string;
  value: string;
  tone: 'default' | 'success' | 'warning' | 'destructive';
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div
        className={cn(
          'num text-xl font-semibold',
          tone === 'success' && 'text-success',
          tone === 'warning' && 'text-warning',
          tone === 'destructive' && 'text-destructive',
        )}
      >
        {value}
      </div>
    </div>
  );
}
